// CompanyEvent - responsive event feed for a company.
// - Uses getEventsByCompanyId(companyId) from ../controller.tsx (defensive check).
// - Renders cards matching SkeletonWebPage layout for desktop and a stacked "skeleton" style for mobile.
// - Opens a right Drawer with event details on card click.
// - Null-checks all event fields and uses a mountedRef to avoid state updates after unmount.
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/material';
import { getEventsByCompanyId, convertToAmPm } from '../controller.tsx';

const CompanyEvent = ({ companyId = 0 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const mountedRef = useRef(false);

  // mobile: match your App's <=416 px rule; useMediaQuery accepts px value
  const isMobile = useMediaQuery('(max-width:416px)');

  useEffect(() => {
    mountedRef.current = true;

    const load = async () => {
      setLoading(true);
      try {
        if (typeof getEventsByCompanyId === 'function') {
          const res = await getEventsByCompanyId(companyId);
          if (!mountedRef.current) return;
          if (Array.isArray(res)) {
            const normalized = res.map((ev) => ({
              id: ev?.id ?? null,
              name: ev?.name ?? 'Untitled event',
              description: ev?.description ?? '',
              location: ev?.location ?? '',
              address: ev?.address ?? '',
              logo: ev?.logo ?? null,
              link: ev?.link ?? null,
              time_string: ev?.time_string ?? ev?.time ?? '',
              date: ev?.date ?? null,
              duration: ev?.duration ?? null,
            }));
            setEvents(normalized);
          } else {
            setEvents([]);
          }
        } else {
          // controller not implemented: warn and leave empty
          // eslint-disable-next-line no-console
          console.warn('getEventsByCompanyId is not defined in controller.tsx');
          setEvents([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading company events:', err);
        if (mountedRef.current) setEvents([]);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    load();

    return () => {
      mountedRef.current = false;
    };
  }, [companyId]);

  const handleOpen = (ev) => {
    setSelectedEvent(ev);
    setIsDrawerOpen(true);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
    setSelectedEvent(null);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!events.length) {
    return (
      <Box py={4}>
        <Typography variant="body2" color="text.secondary">
          No events found for this company.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {events.map((ev, index) => {
        const key = ev.id ?? `${ev.name}-${index}`;
        // Desktop card layout (similar to SkeletonWebPage)
        if (!isMobile) {
          return (
            <Card
              key={key}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                boxShadow: 2,
                cursor: 'pointer',
                '&:hover': { boxShadow: 4 },
                display: 'block',
              }}
              onClick={() => handleOpen(ev)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Box sx={{ width: 80, height: 80, overflow: 'hidden', borderRadius: 2, flexShrink: 0 }}>
                    {ev.logo ? (
                      <img src={ev.logo} alt={ev.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Avatar sx={{ width: '100%', height: '100%' }}>{(ev.name || 'E').charAt(0).toUpperCase()}</Avatar>
                    )}
                  </Box>

                  <Box sx={{ pl: 3, flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {ev.name}
                    </Typography>
                    {ev.location && (
                      <Typography variant="body2" color="text.secondary">
                        {ev.location}
                      </Typography>
                    )}
                    {ev.date && (
                        <Typography variant="body2" color="text.secondary">
                            {formatDate(ev.date)}
                        </Typography>
                    )}
                    {typeof convertToAmPm === 'function' && ev.time_string ? (
                      <Typography variant="body2" color="text.secondary">
                        {convertToAmPm(ev.time_string)}
                      </Typography>
                    ) : (
                      ev.time_string && (
                        <Typography variant="body2" color="text.secondary">
                          {ev.time_string}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        }

        // Mobile card layout (simpler / stacked like skeleton mobile)
        return (
          <Card key={key} sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }} onClick={() => handleOpen(ev)}>
            {ev.logo ? (
              <Box sx={{ width: '100%', height: 160, overflow: 'hidden' }}>
                <img src={ev.logo} alt={ev.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ) : null}
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold">
                {ev.name}
              </Typography>
              {ev.location && (
                <Typography variant="body2" color="text.secondary">
                  {ev.location}
                </Typography>
              )}
              {typeof convertToAmPm === 'function' && ev.time_string ? (
                <Typography variant="body2" color="text.secondary">
                  {convertToAmPm(ev.time_string)}
                </Typography>
              ) : (
                ev.time_string && (
                  <Typography variant="body2" color="text.secondary">
                    {ev.time_string}
                  </Typography>
                )
              )}
              {ev.description && (
                <Typography variant="body2" sx={{ mt: 1 }} noWrap>
                  {ev.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                {ev.link ? (
                  <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); window.open(ev.link, '_blank', 'noopener'); }}>
                    Link
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" disabled>
                    No Link
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, borderRadius: 2, m: 2, p: 2 } }}
      >
        <Box p={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedEvent ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {selectedEvent.logo ? (
                  <img
                    src={selectedEvent.logo}
                    alt="Event Logo"
                    style={{ width: '100%', maxWidth: 400, height: 200, objectFit: 'cover', borderRadius: 8 }}
                  />
                ) : (
                  <Avatar sx={{ width: 120, height: 120 }}>{(selectedEvent.name || 'E').charAt(0).toUpperCase()}</Avatar>
                )}
              </Box>

              <Typography variant="h6" fontWeight="bold">
                {selectedEvent.name}
              </Typography>
              {selectedEvent.description && <Typography sx={{ mt: 1 }}>{selectedEvent.description}</Typography>}

              {(selectedEvent.location || selectedEvent.address) && (
                <Box sx={{ my: 2, cursor: 'pointer' }} onClick={() => {
                  const q = encodeURIComponent(selectedEvent.location || selectedEvent.address || '');
                  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
                }}>
                  {selectedEvent.location && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.location}
                    </Typography>
                  )}
                  {selectedEvent.address && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {selectedEvent.address}
                    </Typography>
                  )}
                </Box>
              )}

              {typeof convertToAmPm === 'function' && selectedEvent.time_string && (
                <Typography>{convertToAmPm(selectedEvent.time_string)}</Typography>
              )}
              {selectedEvent.time_string && !convertToAmPm && <Typography>{selectedEvent.time_string}</Typography>}

              {selectedEvent.link && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="text" color="secondary" onClick={() => window.open(selectedEvent.link, '_blank', 'noopener')}>
                    {selectedEvent.link}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography>Nothing selected</Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default CompanyEvent;